import { NavigateFunction } from 'react-router-dom'
import { v4 as uuidv4, v7 as uuidv7 } from 'uuid'

import { serializeError } from '@/monitoring/diagnostics'
import { AuthService } from '@/services/authService'
import { deviceTokenService } from '@/services/deviceTokenService'
import { POSService } from '@/services/posService'
import { Auth } from '@/types/common/httpRequest'

/**
 * Generates a globally unique session ID with embedded timestamp.
 * Uses UUIDv7 which includes timestamp information in the UUID itself.
 * Format: UUIDv7 string (e.g., "018d3f85-6f3a-7000-8000-123456789abc")
 * The timestamp is embedded in the first part of the UUID.
 *
 * Fallback: If UUIDv7 is not available, uses UUIDv4 with appended Unix timestamp.
 */
export const generateSessionId = (): string => {
    try {
        return uuidv7()
    } catch (error) {
        console.warn('UUIDv7 not available, using fallback', error)
        const timestamp = Date.now()
        return `${uuidv4()}-${timestamp}`
    }
}

/**
 * Verifies or creates cashier JWT authentication token.
 * Note: This is separate from the order session ID (UUIDv7).
 * The JWT is for API authentication, while sessionId tracks user order sessions.
 */
export const verifyCashierAuth = async (navigate: NavigateFunction) => {
    if (!deviceTokenService.isInitialized()) {
        const bootstrapResult = await deviceTokenService.initialize()
        if (bootstrapResult === 'connectivity_retry') {
            navigate('/connectivity-issues', {
                state: {
                    source: 'sessionHelper.verifyCashierAuth.bootstrap',
                    reason: `Token bootstrap: ${bootstrapResult}`
                }
            })
            return
        }
        if (bootstrapResult === 'register') {
            navigate('/register', {
                state: {
                    source: 'sessionHelper.verifyCashierAuth.bootstrap',
                    reason: 'No device token in localStorage or device API, and no registration marker'
                }
            })
            return
        }
    }

    if (deviceTokenService.shouldNavigateToConnectivityIssues()) {
        console.warn(
            'verifyCashierAuth: device token recovery in progress - redirecting to connectivity issues'
        )
        navigate('/connectivity-issues', {
            state: {
                source: 'sessionHelper.verifyCashierAuth.preCheck',
                reason: 'Device token not ready (local API unreachable or empty with prior registration)'
            }
        })
        return
    }

    if (deviceTokenService.shouldNavigateToRegister()) {
        console.warn(
            'verifyCashierAuth: device needs registration - redirecting to register'
        )
        navigate('/register', {
            state: {
                source: 'sessionHelper.verifyCashierAuth.preCheck',
                reason: 'Genuinely unregistered device (no token, no marker, device API empty)'
            }
        })
        return
    }

    try {
        console.log(
            'Welcome page - attempting to verify cashier authentication...'
        )
        await POSService.createSession()
        console.log(
            '✅ Cashier authentication verified successfully on welcome page'
        )
    } catch (error) {
        if (
            error &&
            typeof error === 'object' &&
            'requiresNavigation' in error
        ) {
            const navigationError = error as {
                requiresNavigation: string
                navigationState?: unknown
            }
            console.log(
                '🔄 API error requires navigation to:',
                navigationError.requiresNavigation
            )
            navigate(
                navigationError.requiresNavigation,
                navigationError.navigationState
                    ? { state: navigationError.navigationState }
                    : undefined
            )
        } else if (AuthService.HasToken(Auth.POS)) {
            console.error(
                'Failed to verify cashier authentication with device token in localStorage - redirecting to connectivity issues:',
                error
            )
            navigate('/connectivity-issues', {
                state: {
                    source: 'sessionHelper.verifyCashierAuth',
                    reason: 'POSService.createSession() failed while device token present (may reload from device API on recovery page)',
                    error: serializeError(error)
                }
            })
        } else {
            console.error(
                'Failed to verify cashier authentication with no device token - redirecting to registration:',
                error
            )
            navigate('/register')
        }
    }
}
